import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';

import { listTelemetryEvents, type PortalTelemetryEvent } from '../api.js';

export interface AuditPanelProps {
  tenantId: string;
  /** Owner bearer; null for a non-owner admin (reads via the session cookie). */
  editToken: string | null;
  /**
   * Customer's current tier. Audit logging only emits when the runtime
   * loads the `auditLog` Enterprise feature AND `adminAccess.auditLogEnabled`
   * is true on the config. Free / Pro see an "Enterprise feature" notice
   * so an empty list doesn't read as broken.
   */
  currentTier: 'core' | 'pro' | 'enterprise';
}

const REFRESH_INTERVAL_MS = 30_000;
const PAGE_LIMIT = 200;

/**
 * Friendly labels for known audit action verbs. The renderer's
 * `AuditLog.append({ action, details })` emits action as a dotted
 * verb; this map turns it into something a compliance reader can
 * scan without having to know our internal naming. Unknown verbs
 * fall through to the raw string.
 */
const ACTION_LABELS: Record<string, string> = {
  'admin.exitGesture':   'Admin exit gesture',
  'admin.pinAccepted':   'Admin PIN accepted',
  'admin.pinRejected':   'Admin PIN rejected',
  'admin.overlayOpened': 'Diagnostic overlay opened',
  'admin.overlayClosed': 'Diagnostic overlay closed',
};

/**
 * Enterprise-only audit-log viewer. Pulls the same ingest store as
 * the Telemetry panel but pre-filtered to `category=audit`, with
 * action-aware friendly labels and a per-row "details" pretty-print
 * (audit events ship structured details — tileId, gestureId, etc.).
 *
 * Audit events go through the standard telemetry pipeline: the
 * renderer's `AuditLog.sink` fans every entry out to `TelemetryEmitter`
 * with `category: 'audit'`, which flushes to /telemetry/:tenantId/events
 * on the normal cadence. So this panel doesn't need its own endpoint —
 * just a focused view over the existing telemetry_events table.
 */
export function AuditPanel({
  tenantId,
  editToken,
  currentTier,
}: AuditPanelProps): JSX.Element {
  const [events, setEvents] = useState<PortalTelemetryEvent[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'unauthorized' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState('');

  // Client-side text filter. Matches the action verb + its friendly
  // label + the stringified details (so a deviceId, tileId, or gesture
  // name hits).
  const trimmedQuery = query.trim();
  const filtered = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (q === '') return events;
    return events.filter((e) => auditEventMatches(e, q));
  }, [events, trimmedQuery]);
  const filtering = trimmedQuery !== '';

  const refresh = useCallback(async () => {
    setStatus('loading');
    const res = await listTelemetryEvents({
      tenantId,
      editToken,
      limit: PAGE_LIMIT,
      category: 'audit',
    });
    if (res.kind === 'ok') {
      setEvents(res.events);
      setErrorMsg(null);
      setStatus('ok');
    } else if (res.kind === 'unauthorized') {
      setStatus('unauthorized');
      setErrorMsg('Your edit token was rejected — re-open the editor from the Fleets list to refresh it.');
    } else {
      setStatus('error');
      setErrorMsg(
        res.kind === 'network'
          ? `Network error: ${res.message}`
          : `Unexpected ${String(res.status)} response.`,
      );
    }
  }, [tenantId, editToken]);

  useEffect(() => {
    void refresh();
    const h = window.setInterval(() => {
      void refresh();
    }, REFRESH_INTERVAL_MS);
    return () => {
      window.clearInterval(h);
    };
  }, [refresh]);

  function toggleExpanded(id: number): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const isEnterprise = currentTier === 'enterprise';

  return (
    <section className="telemetry-panel audit-panel" data-testid="audit-panel">
      <header className="telemetry-panel__head">
        <div className="telemetry-panel__head-text">
          <p className="telemetry-panel__sub">
            Tamper-evident record of admin actions, gesture and PIN events,
            and runtime state changes from your fleet. Refreshes every
            30 seconds. Devices keep the last 200 entries in memory and
            flush them through the same telemetry pipeline — see{' '}
            <a
              href="https://mobilauncher.com/docs/security"
              target="_blank"
              rel="noopener noreferrer"
              className="telemetry-panel__doc-link"
            >
              the docs
            </a>
            .
          </p>
        </div>
        <div className="telemetry-panel__controls">
          <input
            type="search"
            className="telemetry-panel__search"
            placeholder="Search entries…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="audit-search"
          />
          <button
            type="button"
            className="builder__btn builder__btn--ghost"
            onClick={() => downloadAuditCsv(filtered, tenantId)}
            disabled={filtered.length === 0}
            title={filtered.length === 0 ? 'No audit entries to export yet' : `Export ${String(filtered.length)} entries as CSV`}
            data-testid="audit-csv"
          >
            ⤓ CSV
          </button>
          <button
            type="button"
            className="builder__btn builder__btn--ghost"
            onClick={() => void refresh()}
            disabled={status === 'loading'}
            data-testid="audit-refresh"
          >
            {status === 'loading' ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </header>

      {errorMsg !== null && (
        <p className="telemetry-panel__error" role="alert">
          {errorMsg}
        </p>
      )}

      {events.length === 0 && status === 'ok' && (
        <AuditEmptyState isEnterprise={isEnterprise} />
      )}

      {events.length > 0 && filtered.length === 0 && (
        <p className="telemetry-panel__empty" data-testid="audit-search-empty">
          No entries match “{trimmedQuery}”.
        </p>
      )}

      {filtered.length > 0 && (
        <table className="telemetry-panel__table audit-panel__table" data-testid="audit-table">
          <thead>
            <tr>
              <th className="telemetry-panel__col-time">When</th>
              <th className="audit-panel__col-device">Device</th>
              <th className="audit-panel__col-action">Action</th>
              <th className="audit-panel__col-details">Details</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => {
              const auditAt = extractAuditAt(e);
              const deviceId = extractDeviceId(e);
              const details = extractDetails(e);
              const label = ACTION_LABELS[e.name] ?? e.name;
              const isOpen = expanded.has(e.id);
              return (
                <tr key={e.id} data-testid="audit-row">
                  <td className="telemetry-panel__col-time">
                    <time dateTime={new Date(auditAt ?? e.receivedAt).toISOString()}>
                      {formatRelative(auditAt ?? e.receivedAt)}
                    </time>
                  </td>
                  <td className="audit-panel__col-device">
                    {deviceId === null ? (
                      <span className="telemetry-panel__muted">—</span>
                    ) : (
                      <code className="audit-panel__device">{shortDevice(deviceId)}</code>
                    )}
                  </td>
                  <td className="audit-panel__col-action">
                    <span className="audit-panel__verb">{label}</span>
                    {label !== e.name && (
                      <span className="audit-panel__verb-raw" title={e.name}>
                        {e.name}
                      </span>
                    )}
                  </td>
                  <td className="audit-panel__col-details">
                    {details === null ? (
                      <span className="telemetry-panel__muted">—</span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="audit-panel__details-toggle"
                          onClick={() => toggleExpanded(e.id)}
                          aria-expanded={isOpen}
                          data-testid="audit-row-expand"
                        >
                          {isOpen ? '▾' : '▸'} {summarizeDetails(details)}
                        </button>
                        {isOpen && (
                          <pre className="audit-panel__details-pre">
                            {JSON.stringify(details, null, 2)}
                          </pre>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {filtered.length > 0 && (
        <p className="telemetry-panel__foot">
          {filtering
            ? `Showing ${String(filtered.length)} of ${String(events.length)} loaded entries.`
            : `Showing the ${String(events.length)} most recent ${events.length === 1 ? 'entry' : 'entries'}. Older entries age out after ~14 days — wire telemetry.endpoint to your own collector for a long-term tamper-evident store.`}
        </p>
      )}
    </section>
  );
}

/**
 * Free-text match over an audit entry: the action verb, its friendly
 * label, and the stringified attrs/details (so a deviceId, tileId, or
 * gesture name hits).
 */
function auditEventMatches(e: PortalTelemetryEvent, q: string): boolean {
  if (e.name.toLowerCase().includes(q)) return true;
  const label = ACTION_LABELS[e.name];
  if (label !== undefined && label.toLowerCase().includes(q)) return true;
  if (e.attrs !== undefined) {
    try {
      if (JSON.stringify(e.attrs).toLowerCase().includes(q)) return true;
    } catch {
      // non-serializable attrs — skip
    }
  }
  return false;
}

function AuditEmptyState({ isEnterprise }: { isEnterprise: boolean }): JSX.Element {
  if (!isEnterprise) {
    return (
      <div className="telemetry-panel__empty" data-testid="audit-empty-locked">
        <p>
          <strong>The audit log is an Enterprise feature.</strong> Free
          and Pro devices don't capture admin / gesture / PIN events to
          this sink — but your config already ships with{' '}
          <code>adminAccess.auditLogEnabled</code> ready to flip on, so
          devices start reporting here the moment you upgrade.
        </p>
      </div>
    );
  }
  return (
    <div className="telemetry-panel__empty" data-testid="audit-empty">
      <p>
        <strong>No audit entries yet.</strong> Make sure{' '}
        <code>adminAccess.auditLogEnabled</code> is true on at least one
        device, then trigger an admin gesture or PIN entry on-device.
        Entries land here on the next telemetry flush (60 seconds by default).
      </p>
    </div>
  );
}

/** Audit attrs ship `at: <iso>` from the renderer-side `AuditEntry`. */
function extractAuditAt(e: PortalTelemetryEvent): number | null {
  if (e.attrs === null || typeof e.attrs !== 'object') return null;
  const at = (e.attrs as Record<string, unknown>)['at'];
  if (typeof at !== 'string') return null;
  const parsed = Date.parse(at);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Device id rides in attrs.deviceId when the emitter was constructed
 * with `defaultAttrs: { deviceId }` — which the renderer does on
 * registered devices. Undefined for legacy / single-device deployments.
 */
function extractDeviceId(e: PortalTelemetryEvent): string | null {
  if (e.attrs === null || typeof e.attrs !== 'object') return null;
  const id = (e.attrs as Record<string, unknown>)['deviceId'];
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/**
 * The renderer wraps the original `AuditEntry.details` under
 * `attrs.details` (engine.ts:181). Unwrap so the table shows the
 * structured payload directly instead of the `{ at, details: {...} }`
 * envelope.
 */
function extractDetails(e: PortalTelemetryEvent): unknown {
  if (e.attrs === null || typeof e.attrs !== 'object') return null;
  const obj = e.attrs as Record<string, unknown>;
  const details = obj['details'];
  if (details !== undefined) return details;
  // Fall back to the whole attrs blob minus our envelope keys, in case
  // a future audit caller emits flat-shaped attrs.
  const rest = { ...obj };
  delete rest['at'];
  delete rest['deviceId'];
  return Object.keys(rest).length === 0 ? null : rest;
}

/** One-line summary of a details object — key=value pairs comma-joined. */
function summarizeDetails(details: unknown): string {
  if (details === null || typeof details !== 'object') return String(details);
  const obj = details as Record<string, unknown>;
  const parts: string[] = [];
  for (const k of Object.keys(obj).slice(0, 4)) {
    const v = obj[k];
    let display: string;
    if (typeof v === 'string') display = v.length > 24 ? `${v.slice(0, 24)}…` : v;
    else if (typeof v === 'number' || typeof v === 'boolean') display = String(v);
    else display = '…';
    parts.push(`${k}=${display}`);
  }
  return parts.length === 0 ? '(empty)' : parts.join(', ');
}

function shortDevice(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-3)}` : id;
}

function formatRelative(epochMs: number): string {
  const diffSec = Math.max(0, Math.round((Date.now() - epochMs) / 1000));
  if (diffSec < 60) return `${String(diffSec)}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${String(diffMin)}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${String(diffHr)}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${String(diffDay)} days ago`;
  return new Date(epochMs).toLocaleDateString();
}

/**
 * CSV export tailored for audit review. Columns chosen to satisfy a
 * compliance auditor's first ask: when, who (device), what, full details.
 * Both audit-side and server-receive timestamps are included so the
 * reader can spot clock skew. CRLF for Excel-on-Windows.
 */
function downloadAuditCsv(events: PortalTelemetryEvent[], tenantId: string): void {
  const header = ['audit_at_iso', 'received_at_iso', 'device_id', 'action', 'details_json'];
  const rows = events.map((e) => {
    const auditAt = extractAuditAt(e);
    const deviceId = extractDeviceId(e);
    const details = extractDetails(e);
    return [
      auditAt === null ? '' : new Date(auditAt).toISOString(),
      new Date(e.receivedAt).toISOString(),
      deviceId ?? '',
      e.name,
      details === null ? '' : JSON.stringify(details),
    ];
  });
  const csv = [header, ...rows].map((row) => row.map(escapeCsvField).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mobilauncher-audit-${tenantId}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escapeCsvField(s: string): string {
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
